import React, { useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { IHomeAwayTeams } from "@data-types/sport-stats/SportStatsTypes";
import { EventContentSkeleton } from "@features/offer/mobile/shared/event/components";
import PressureIndexStore from "@state/stores/offer/statistics/PressureIndexStore";
import classnames from "classnames";
import type { HighchartsReactRefObject } from "highcharts-react-official";
import { observer } from "mobx-react";

import { chartConfig } from "../chartConfig";

function createPeriodLine(options: {
	value: number;
	width?: number;
	text?: string;
	color?: string;
	dashStyle?: string;
}) {
	const { value, color, dashStyle, width = 1 } = options;

	return {
		color,
		width,
		value,
		dashStyle,
		zIndex: 1,
	};
}

type HighchartsReactType = typeof import("highcharts-react-official").default;

export default observer(function PressureIndexChart(props: {
	store: PressureIndexStore;
	homeAwayTeams: IHomeAwayTeams | null;
}) {
	const { t } = useTranslation();
	const [showInfoSection, setShowInfoSection] = useState(false);
	const chartComponent = useRef<HighchartsReactRefObject | null>(null);
	const [chartModules, setChartModules] = useState<{
		Highcharts: typeof import("highcharts") | null;
		HighchartsReact: HighchartsReactType | null;
	}>({ Highcharts: null, HighchartsReact: null });

	useEffect(() => {
		let mounted = true;
		Promise.all([
			import("highcharts"),
			import("highcharts-react-official"),
		]).then(([highchartsModule, highchartsReactModule]) => {
			if (mounted) {
				setChartModules({
					Highcharts: highchartsModule,
					HighchartsReact: highchartsReactModule.default,
				});
			}
		});
		return () => {
			mounted = false;
		};
	}, []);

	const {
		currentPeriods,
		currentChartTime,
		valueYMax,
		xAxisValue,
		showValues,
	} = props.store;

	const periods = currentPeriods.map((period) =>
		createPeriodLine({
			value: period.value,
			color: "#C6C6C6",
			dashStyle: "Dash",
		})
	);
	const currentTime = currentChartTime
		? [
				createPeriodLine({
					value: currentChartTime,
					color: "#FF0357",
					dashStyle: "Solid",
					width: 4,
				}),
			]
		: [];

	const coloredValues = showValues.map((item, index) => ({
		y: item,
		color: item > 0 ? "#CC0615" : "#5B5B67",
		key: index + 1,
		teamName:
			item < 0
				? props.homeAwayTeams?.awayTeam
				: props.homeAwayTeams?.homeTeam,
	}));

	const plotLines = [...periods, ...currentTime];

	const options = {
		...chartConfig,
		xAxis: {
			...chartConfig.xAxis,
			max: xAxisValue,
			plotLines,
			plotBands: [
				{
					from: -10,
					to: currentChartTime,
					color: {
						linearGradient: [0, 0, 0, "100%"],
						stops: [
							[0, "rgba(204, 6, 21, 0.05)"], // start
							[0.49, "rgba(204, 6, 21, 0.05)"], // middle
							[0.5, "rgba(91, 91, 103, 0.05)"], // middle
							[1, "rgba(91, 91, 103, 0.05)"], // end
						],
					},
				},
			],
		},
		yAxis: {
			...chartConfig.yAxis,
			max: valueYMax,
			min: -valueYMax,
		},
		tooltip: false,
		series: [
			{
				data: coloredValues,
				animation: false,
			},
		],
	};

	useEffect(() => {
		if (!chartModules.Highcharts || !chartModules.HighchartsReact) {
			return;
		}

		props.store.chartRef = chartComponent.current?.chart || null;

		if (chartComponent.current) {
			chartComponent.current.chart.reflow();
		}

		return () => {
			props.store.chartRef = null;
		};
	}, [chartModules.Highcharts, chartModules.HighchartsReact]);

	if (showValues == null || options == null || showValues.length === 0) {
		return null;
	}

	const { Highcharts, HighchartsReact } = chartModules;

	const isWeb = window.innerWidth > 768;

	return (
		<div
			className={classnames("card card--raised card--stats", {
				/* "u-mar--top--reset u-mar--bottom--xsml": isWeb,
				"card--stats__wrapper--secondary": !isWeb, */
			})}
		>
			<div
				className="card__header card--stats__header"
				onClick={() => setShowInfoSection(!showInfoSection)}
				role="button"
				aria-expanded={showInfoSection}
				aria-controls="pressureIndexInfo"
			>
				<div className="card__title card__title--base card--stats__heading">
					{t("EVENT.STATISTICS.PRESSURE_INDEX_TITLE")}
				</div>

				<i
					className={classnames(
						"card--stats__info u-icon u-icon--xsml",
						{
							"u-icon--info-circle u-color--text--tertiary":
								!showInfoSection,
							"u-icon--info-circle--bold is-expanded":
								showInfoSection,
						},
						"u-mar--reset"
					)}
				/>
			</div>

			{isWeb && showInfoSection && (
				<div
					id="pressureIndexInfo"
					className="card__body u-padd--top--reset"
				>
					<div className="message message--sml message--info u-type--xsml">
						{t("EVENT.STATISTICS.PRESSURE_INDEX_INFO")}
					</div>
				</div>
			)}

			<div className="card--stats__body">
				{!isWeb && showInfoSection && (
					<div
						id="pressureIndexInfo"
						className="message message--sml message--info u-type--xsml"
					>
						{t("EVENT.STATISTICS.PRESSURE_INDEX_INFO")}
					</div>
				)}

				{Highcharts && HighchartsReact ? (
					<HighchartsReact
						ref={chartComponent}
						highcharts={Highcharts}
						options={options}
						containerProps={{ className: "chart--pressure" }}
					/>
				) : (
					<EventContentSkeleton rowCount={1} />
				)}
			</div>
		</div>
	);
});
